import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import { Alert, LoadingState } from '../ui';
import { Line, Pie } from 'react-chartjs-2';
import {
  buildProjectStatisticsRangeParams,
  getProjectStatisticsPreset,
  validateProjectStatisticsRange,
} from './projectStatisticsDateRange';
import { getProjectActivityReviewStats } from '../../services/activityReviewService';

// Helper: Format large numbers with K/M suffixes
const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

// Helper: Prepare timeline chart data
const prepareTimelineChartData = (timeline) => {
  if (!timeline || timeline.length === 0) {
    return {
      labels: [],
      datasets: []
    };
  }

  // Format dates for X-axis
  const labels = timeline.map(item => {
    const date = new Date(item.date.length === 10 ? `${item.date}T00:00:00Z` : item.date);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  });

  // Real data: Mentions from database
  const mentionsData = timeline.map(item => item.mentions);

  return {
    labels,
    datasets: [
      {
        label: 'Mentions',
        data: mentionsData,
        borderColor: 'rgb(59, 130, 246)', // Blue
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.3, // Smooth curve
        fill: true
      }
    ]
  };
};

// Helper: Prepare top keywords pie chart data
const prepareKeywordsPieChartData = (topKeywords) => {
  if (!topKeywords || topKeywords.length === 0) {
    return {
      labels: [],
      datasets: []
    };
  }

  // Color palette for keywords (vibrant colors)
  const colors = [
    'rgba(59, 130, 246, 0.8)',   // Blue
    'rgba(34, 197, 94, 0.8)',    // Green
    'rgba(251, 146, 60, 0.8)',   // Orange
    'rgba(168, 85, 247, 0.8)',   // Purple
    'rgba(236, 72, 153, 0.8)',   // Pink
    'rgba(14, 165, 233, 0.8)',   // Cyan
    'rgba(245, 158, 11, 0.8)',   // Amber
  ];

  const borderColors = [
    'rgb(59, 130, 246)',
    'rgb(34, 197, 94)',
    'rgb(251, 146, 60)',
    'rgb(168, 85, 247)',
    'rgb(236, 72, 153)',
    'rgb(14, 165, 233)',
    'rgb(245, 158, 11)',
  ];

  return {
    labels: topKeywords.map(k => k.keyword),
    datasets: [
      {
        data: topKeywords.map(k => k.count),
        backgroundColor: colors.slice(0, topKeywords.length),
        borderColor: borderColors.slice(0, topKeywords.length),
        borderWidth: 2,
      }
    ]
  };
};

const ProjectStatistics = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePreset, setActivePreset] = useState('7d');
  const [draftRange, setDraftRange] = useState(() => getProjectStatisticsPreset('7d'));
  const [appliedRange, setAppliedRange] = useState(() => getProjectStatisticsPreset('7d'));
  const [rangeError, setRangeError] = useState(null);

  // Statistics data
  const [overview, setOverview] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [topKeywords, setTopKeywords] = useState([]);

  // Active section for display
  const [activeSection, setActiveSection] = useState('overview'); // overview, keywords, combinations, groups

  const activityRangeParams = buildProjectStatisticsRangeParams(appliedRange);
  const reviewStatsQuery = useQuery(
    ['activityReviewStats', projectId, activityRangeParams.from || '', activityRangeParams.to || ''],
    () => getProjectActivityReviewStats(projectId, activityRangeParams),
    { enabled: Boolean(projectId) },
  );
  const pendingReviewCount = Number(reviewStatsQuery.data?.pending || 0);

  // Fetch all statistics
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const fetchStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: {
            'x-auth-token': token
          },
          params: buildProjectStatisticsRangeParams(appliedRange)
        };

        // Fetch all statistics in parallel
        const [overviewRes, keywordsRes, combinationsRes, groupsRes] = await Promise.all([
          axios.get(`/api/projects/${projectId}/statistics/overview`, config),
          axios.get(`/api/projects/${projectId}/statistics/keywords`, config),
          axios.get(`/api/projects/${projectId}/statistics/combinations`, config),
          axios.get(`/api/projects/${projectId}/statistics/groups`, config)
        ]);

        if (cancelled) return;

        setOverview(overviewRes.data);
        setKeywords(keywordsRes.data.keywords || []);
        setCombinations(combinationsRes.data.combinations || []);
        setGroups(groupsRes.data.groups || []);
        setTimeline(overviewRes.data.timeline || []);
        setTopKeywords(overviewRes.data.topKeywords || []);
      } catch (err) {
        if (cancelled) return;

        console.error('Error fetching statistics:', err);
        setError(err.response?.data?.error || 'Failed to load statistics');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStatistics();
    return () => {
      cancelled = true;
    };
  }, [projectId, appliedRange.from, appliedRange.to]);

  if (loading) {
    return <LoadingState message="Loading statistics..." />;
  }

  if (error) {
    return (
      <Alert variant="error" className="mt-4">
        {error}
      </Alert>
    );
  }

  const applyPreset = (preset) => {
    const range = getProjectStatisticsPreset(preset);
    setActivePreset(preset);
    setDraftRange(range);
    setAppliedRange(range);
    setRangeError(null);
  };

  const applyCustomRange = () => {
    const validationError = validateProjectStatisticsRange(draftRange);
    if (validationError) {
      setRangeError(validationError);
      return;
    }

    setActivePreset('custom');
    setAppliedRange(draftRange);
    setRangeError(null);
  };

  const DateRangeSelector = () => {
    const presets = [
      ['7d', '7 days'],
      ['30d', '30 days'],
      ['month', 'Calendar month'],
      ['all', 'All time'],
    ];

    return (
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex flex-wrap gap-2 mb-4">
          {presets.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => applyPreset(value)}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                activePreset === value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-sm font-medium text-gray-700">
            From
            <input
              type="date"
              value={draftRange.from}
              onChange={(event) => {
                setDraftRange((current) => ({ ...current, from: event.target.value }));
                setActivePreset('custom');
              }}
              className="mt-1 block rounded-md border-gray-300 shadow-sm"
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            To
            <input
              type="date"
              value={draftRange.to}
              onChange={(event) => {
                setDraftRange((current) => ({ ...current, to: event.target.value }));
                setActivePreset('custom');
              }}
              className="mt-1 block rounded-md border-gray-300 shadow-sm"
            />
          </label>
          <button
            type="button"
            onClick={applyCustomRange}
            className="px-4 py-2 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700"
          >
            Apply
          </button>
        </div>

        <p className="mt-2 text-xs text-gray-500">
          UTC calendar days. From and To are both inclusive.
        </p>
        {rangeError && <p className="mt-2 text-sm text-red-600">{rangeError}</p>}
      </div>
    );
  };

  const UnavailableMetricCard = ({ title, description, className = '' }) => (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-3xl font-bold text-gray-400 mt-2">—</p>
      <p className="mt-4 text-sm text-gray-600">{description}</p>
    </div>
  );

  const ActivityMetricCard = ({ title, value, description, className = '' }) => (
    <div className={`rounded-lg border border-gray-200 bg-white p-4 ${className}`}>
      <h4 className="text-sm font-medium text-gray-600">{title}</h4>
      <p className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(value)}</p>
      {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
    </div>
  );

  const activityRangeLabel = activityRangeParams.from && activityRangeParams.to
    ? `${activityRangeParams.from} to ${activityRangeParams.to} UTC`
    : 'All time';

  const ReviewedAgentActivity = () => {
    if (reviewStatsQuery.isLoading) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <h3 className="text-lg font-semibold text-gray-900">Reviewed agent activity</h3>
          <p className="mt-2 text-sm text-gray-500">Loading reviewed activity for {activityRangeLabel}…</p>
        </div>
      );
    }

    if (reviewStatsQuery.isError) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5" role="status">
          <h3 className="text-lg font-semibold text-red-800">Reviewed agent activity is unavailable</h3>
          <p className="mt-1 text-sm text-red-700">
            Other project statistics are still available. Try refreshing this page later.
          </p>
        </div>
      );
    }

    const stats = reviewStatsQuery.data || {};
    const infoEvents = stats.infoEvents || {};

    return (
      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-5" aria-labelledby="reviewed-agent-activity-heading">
        <div className="mb-4 sm:flex sm:items-start sm:justify-between sm:gap-4">
          <div>
            <h3 id="reviewed-agent-activity-heading" className="text-lg font-semibold text-gray-900">
              Reviewed agent activity
            </h3>
            <p className="mt-1 text-sm text-gray-600">Confirmed work for {activityRangeLabel}</p>
          </div>
          <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 sm:mt-0 sm:max-w-xs">
            {pendingReviewCount > 0
              ? `${pendingReviewCount} pending ${pendingReviewCount === 1 ? 'candidate is' : 'candidates are'} not included.`
              : 'Pending candidates are not included in these figures.'}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ActivityMetricCard
            title="Info events"
            value={Number(infoEvents.total || 0)}
            description={`${Number(infoEvents.singleton || 0)} single · ${Number(infoEvents.multi || 0)} chains`}
            className="border-l-4 border-l-blue-500"
          />
          <ActivityMetricCard
            title="Warm-ups"
            value={Number(stats.warmup || 0)}
            description="Account reality and natural activity"
          />
          <ActivityMetricCard
            title="Account setup"
            value={Number(stats.accountIntegration || 0)}
            description="Initial account introduction"
          />
          <ActivityMetricCard
            title="Other project work"
            value={Number(stats.otherWork || 0)}
            description="Reviewed work outside the categories above"
          />
        </div>
      </section>
    );
  };

  // Overview section
  const OverviewSection = () => (
      <div className="space-y-6">
        <ReviewedAgentActivity />

        {/* ROW 1: Activity & Engagement */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 1.1: Total Matches */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Matches</h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {formatNumber(overview?.totalMatches || 0)}
            </p>
            {overview?.comparison?.available ? (
              <div className="mt-4 text-sm text-gray-600">
                <div>
                  Previous period:{' '}
                  <span className="font-semibold">
                    {formatNumber(overview.comparison.totalMatches.previous)}
                  </span>
                </div>
                <div>
                  Change:{' '}
                  <span className="font-semibold">
                    {overview.comparison.totalMatches.absoluteChange > 0 ? '+' : ''}
                    {overview.comparison.totalMatches.absoluteChange}
                    {overview.comparison.totalMatches.percentageChange === null
                      ? ''
                      : ` (${overview.comparison.totalMatches.percentageChange > 0 ? '+' : ''}${overview.comparison.totalMatches.percentageChange}%)`}
                  </span>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-gray-600">All-time total</p>
            )}
          </div>

          {/* Card 1.2: Unique Groups */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Unique Groups</h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {overview?.uniqueGroups || 0}
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Groups where keywords have been matched
            </p>
          </div>

          {/* Card 1.3: Unique Keywords */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Unique Keywords</h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {overview?.uniqueKeywords || 0}
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Different keywords that have matched
            </p>
          </div>
        </div>

        {/* ROW 2: Audience & Team Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 2.1: Estimated Reach */}
          <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Estimated Reach</h3>
              <span className="text-2xl">📊</span>
            </div>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {formatNumber(overview?.totalReach || 0)}
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Heuristic: 15% of members in groups with matches in the selected range
            </p>
          </div>

          <UnavailableMetricCard
            title="Active Agents"
            description="Unavailable: project-scoped agent activity is not recorded for this date range."
            className="border-l-4 border-gray-300"
          />
          <UnavailableMetricCard
            title="Agent Messages"
            description="Unavailable: project-scoped agent response telemetry is not connected."
            className="border-l-4 border-gray-300"
          />
        </div>

        {/* ROW 3: Performance Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 3.1: Active Coverage */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Active Coverage</h3>
            <p className="text-lg font-bold text-gray-900 mt-2">
              {overview?.activeCoverage?.groupsWithMatches || 0} groups with keyword mentions
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Selected-range matches out of {overview?.activeCoverage?.totalGroups || 0} project groups
            </p>
          </div>

          <UnavailableMetricCard
            title="Response Rate"
            description="Unavailable: responses cannot be reliably attributed to selected matches."
          />
          <UnavailableMetricCard
            title="Avg Response Time"
            description="Unavailable: no project-scoped response latency metric is recorded."
          />
        </div>

        {/* ROW 4: Charts - 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* LEFT: Timeline Chart */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">
                Mentions Timeline
              </h3>
            </div>

            {timeline.length > 0 ? (
              <Line
                data={prepareTimelineChartData(timeline)}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  interaction: {
                    mode: 'index',
                    intersect: false,
                  },
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        padding: 15
                      }
                    },
                    tooltip: {
                      callbacks: {
                        title: (context) => {
                          return `Date: ${context[0].label}`;
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      display: true,
                      title: {
                        display: true,
                        text: 'Period'
                      },
                      grid: {
                        display: false
                      }
                    },
                    y: {
                      display: true,
                      title: {
                        display: true,
                        text: 'Count'
                      },
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.05)'
                      }
                    }
                  }
                }}
              />
            ) : (
              <div className="text-center text-gray-500 py-8">
                No data available for selected period
              </div>
            )}
          </div>

          {/* RIGHT: Top Keywords Pie Chart */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">
                Top Keywords
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Distribution of mentions by keyword
              </p>
            </div>

            {topKeywords.length > 0 ? (
              <div className="flex justify-center">
                <div className="w-full max-w-md">
                  <Pie
                    data={prepareKeywordsPieChartData(topKeywords)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: 'right',
                          labels: {
                            usePointStyle: true,
                            padding: 12,
                            font: {
                              size: 11
                            }
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = ((value / total) * 100).toFixed(1);
                              return `${label}: ${value} (${percentage}%)`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No data available for selected period
              </div>
            )}
          </div>
        </div>
      </div>
  );

  // Keywords section
  const KeywordsSection = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Keyword
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Matches
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Percentage
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {keywords.length === 0 ? (
            <tr>
              <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                No keyword matches found for this period
              </td>
            </tr>
          ) : (
            keywords.map((keyword, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {keyword.keyword}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      keyword.type === 'primary'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {keyword.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {keyword.count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {keyword.percentage}%
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // Combinations section
  const CombinationsSection = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Primary Keywords
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Secondary Keywords
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Matches
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {combinations.length === 0 ? (
            <tr>
              <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                No keyword combinations found for this period
              </td>
            </tr>
          ) : (
            combinations.map((combo, index) => (
              <tr key={index}>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {combo.primary.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {combo.primary.map((kw, i) => (
                        <span
                          key={i}
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">None</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {combo.secondary.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {combo.secondary.map((kw, i) => (
                        <span
                          key={i}
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">None</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {combo.count}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // Groups section
  const GroupsSection = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Group Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Username
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Members
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Matches
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Percentage
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {groups.length === 0 ? (
            <tr>
              <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                No group matches found for this period
              </td>
            </tr>
          ) : (
            groups.map((group, index) => (
              <tr key={index}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  {group.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {group.username ? (
                    <a
                      href={`https://t.me/${group.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800"
                    >
                      @{group.username}
                    </a>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {group.memberCount?.toLocaleString() || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {group.matchCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {group.percentage}%
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="mt-4">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Project Statistics</h2>

        {pendingReviewCount > 0 && (
          <Alert variant="warning" title={`${pendingReviewCount} ${pendingReviewCount === 1 ? 'activity needs' : 'activities need'} review`} className="mb-4">
            These candidates are excluded from confirmed figures until someone reviews them in Activity review.
          </Alert>
        )}

        <DateRangeSelector />

        {/* Section tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8" aria-label="Statistics Sections">
            <button
              onClick={() => setActiveSection('overview')}
              className={`${
                activeSection === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveSection('keywords')}
              className={`${
                activeSection === 'keywords'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Keywords ({keywords.length})
            </button>
            <button
              onClick={() => setActiveSection('combinations')}
              className={`${
                activeSection === 'combinations'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Combinations ({combinations.length})
            </button>
            <button
              onClick={() => setActiveSection('groups')}
              className={`${
                activeSection === 'groups'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Groups ({groups.length})
            </button>
          </nav>
        </div>

        {/* Section content */}
        <div className="mt-4">
          {activeSection === 'overview' && <OverviewSection />}
          {activeSection === 'keywords' && <KeywordsSection />}
          {activeSection === 'combinations' && <CombinationsSection />}
          {activeSection === 'groups' && <GroupsSection />}
        </div>
      </div>
    </div>
  );
};

export default ProjectStatistics;
